import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BackendClient } from "@equipped/core/backend/client";

export type DocKind = "catalog" | "manifest";

interface DocState {
  kind: DocKind;
  /** `null` while the id is still blank on a brand-new, unsaved draft. */
  id: string;
  draft: Record<string, unknown>;
  /** The last-loaded-or-saved snapshot this draft is compared against for dirty-state. */
  saved: Record<string, unknown> | null;
  loading: boolean;
  loadError: string | null;
}

let newDocSeq = 0;

/**
 * Holds every open document's draft state for the manifest/catalog editor
 * (docs/343-ADMIN-MANIFEST-BUILDER-REDESIGN.md v0 §2/§4) — indices via react-query,
 * full bodies loaded on demand (catalog docs eagerly — an expected-small,
 * one-admin dataset — pack manifests lazily, on first selection). Documents are
 * keyed by a stable "doc key" (`catalog:<id>`/`manifest:<id>`, or a synthetic
 * `catalog:__new_1__`/`manifest:__new_1__` for an unsaved draft) — NOT by the
 * document's own editable `id` field, so renaming `id` mid-edit doesn't lose the
 * open tab.
 */
export function useEditorTree(client: BackendClient | null, token: string | null, configuredBackendUrl: string | null) {
  const queryClient = useQueryClient();
  const [docs, setDocs] = useState<Record<string, DocState>>({});
  const backendUrl = configuredBackendUrl ?? "";

  const manifestIndexQuery = useQuery({
    queryKey: ["admin-manifests"],
    queryFn: () => client!.adminListManifests(token!),
    enabled: !!client && !!token,
  });
  const catalogIndexQuery = useQuery({
    queryKey: ["admin-catalog-docs"],
    queryFn: () => client!.adminListCatalogDocs(token!),
    enabled: !!client && !!token,
  });

  const manifestIndex = useMemo(() => manifestIndexQuery.data ?? [], [manifestIndexQuery.data]);
  const catalogIndex = useMemo(() => catalogIndexQuery.data ?? [], [catalogIndexQuery.data]);

  // Eagerly load every catalog-doc body (small dataset, docs/343 §4) so the tree
  // can resolve nested pack/catalog references without a per-node fetch. ONE
  // cancellation flag for the whole effect run (not per-row) — a `useEffect`
  // callback can only return a single cleanup, so per-iteration cleanups would
  // silently only cover the LAST row otherwise.
  useEffect(() => {
    if (!client || !token) return undefined;
    let cancelled = false;
    for (const row of catalogIndex) {
      const key = `catalog:${row.id}`;
      if (docs[key]) continue; // already loaded/loading/edited/created this session
      setDocs((cur) => ({ ...cur, [key]: { kind: "catalog", id: row.id, draft: {}, saved: null, loading: true, loadError: null } }));
      client
        .adminGetCatalogDoc(token, row.id)
        .then((detail) => {
          if (cancelled) return;
          const body = (detail.catalog ?? {}) as Record<string, unknown>;
          setDocs((cur) =>
            // Only settle a row that's still in this effect's own "loading" state —
            // a save/discard that happened meanwhile must not be clobbered.
            cur[key]?.loading
              ? { ...cur, [key]: { kind: "catalog", id: row.id, draft: body, saved: body, loading: false, loadError: null } }
              : cur,
          );
        })
        .catch((e: Error) => {
          if (cancelled) return;
          setDocs((cur) => (cur[key]?.loading ? { ...cur, [key]: { ...cur[key], loading: false, loadError: e.message } } : cur));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [client, token, catalogIndex]);

  const loadPack = useCallback(
    (id: string) => {
      const key = `manifest:${id}`;
      if (!client || !token || docs[key]) return;
      setDocs((cur) => ({ ...cur, [key]: { kind: "manifest", id, draft: {}, saved: null, loading: true, loadError: null } }));
      client
        .adminGetManifest(token, id)
        .then((detail) => {
          const body = (detail.manifest ?? {}) as Record<string, unknown>;
          setDocs((cur) => ({ ...cur, [key]: { kind: "manifest", id, draft: body, saved: body, loading: false, loadError: null } }));
        })
        .catch((e: Error) => {
          setDocs((cur) => ({ ...cur, [key]: { ...cur[key], loading: false, loadError: e.message } }));
        });
    },
    [client, token, docs],
  );

  const createCatalog = useCallback(() => {
    newDocSeq += 1;
    const key = `catalog:__new_${newDocSeq}__`;
    setDocs((cur) => ({
      ...cur,
      [key]: {
        kind: "catalog",
        id: "",
        draft: { specVersion: 1, id: "", title: "", entries: [] },
        saved: null,
        loading: false,
        loadError: null,
      },
    }));
    return key;
  }, []);

  const createPack = useCallback(() => {
    newDocSeq += 1;
    const key = `manifest:__new_${newDocSeq}__`;
    setDocs((cur) => ({
      ...cur,
      [key]: {
        kind: "manifest",
        id: "",
        draft: { specVersion: 1, id: "", title: "", version: "1.0.0", lessons: [] },
        saved: null,
        loading: false,
        loadError: null,
      },
    }));
    return key;
  }, []);

  const updateDraft = useCallback((key: string, next: Record<string, unknown>) => {
    setDocs((cur) => (cur[key] ? { ...cur, [key]: { ...cur[key], draft: next } } : cur));
  }, []);

  const discard = useCallback((key: string) => {
    setDocs((cur) => {
      const d = cur[key];
      if (!d) return cur;
      if (d.saved === null) {
        // An unsaved new-doc discard just drops the whole open document.
        const next = { ...cur };
        delete next[key];
        return next;
      }
      return { ...cur, [key]: { ...d, draft: d.saved } };
    });
  }, []);

  const save = useCallback(
    async (key: string): Promise<{ ok: true; newKey: string } | { ok: false; error: string }> => {
      const d = docs[key];
      if (!d || !client || !token) return { ok: false, error: "not ready" };
      try {
        if (d.kind === "manifest") {
          const row = await client.adminSaveManifest(token, d.draft);
          const newKey = `manifest:${row.id}`;
          setDocs((cur) => {
            const next = { ...cur };
            if (key !== newKey) delete next[key];
            next[newKey] = { kind: "manifest", id: row.id, draft: d.draft, saved: d.draft, loading: false, loadError: null };
            return next;
          });
          await queryClient.invalidateQueries({ queryKey: ["admin-manifests"] });
          return { ok: true, newKey };
        }
        const row = await client.adminSaveCatalogDoc(token, d.draft);
        const newKey = `catalog:${row.id}`;
        setDocs((cur) => {
          const next = { ...cur };
          if (key !== newKey) delete next[key];
          next[newKey] = { kind: "catalog", id: row.id, draft: d.draft, saved: d.draft, loading: false, loadError: null };
          return next;
        });
        await queryClient.invalidateQueries({ queryKey: ["admin-catalog-docs"] });
        return { ok: true, newKey };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    [docs, client, token, queryClient],
  );

  return {
    manifestIndex,
    catalogIndex,
    manifestIndexLoading: manifestIndexQuery.isLoading,
    catalogIndexLoading: catalogIndexQuery.isLoading,
    docs,
    backendUrl,
    loadPack,
    createCatalog,
    createPack,
    updateDraft,
    discard,
    save,
  };
}

export type EditorTree = ReturnType<typeof useEditorTree>;
